import AddIcon from "@mui/icons-material/Add";
import MicIcon from "@mui/icons-material/Mic";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { useState } from "react";

import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  createMediaSegment,
  createPauseSegment,
  createVoiceSegment,
  useScriptEditor,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";

interface SegmentAddButtonProps {
  /** Insert after this index. Undefined = append to end. */
  afterIndex?: number;
}

/**
 * Small "+" button shown between two segments (and at the end of the editor).
 * Opens a menu with options to add Voice / Pause / Media segment.
 */
export const SegmentAddButton = ({
  afterIndex,
}: SegmentAddButtonProps): React.ReactNode => {
  const { t } = useResource();
  const { addSegment, uiDefaults, isEditable } = useScriptEditor();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleAddVoice = () => {
    addSegment(
      createVoiceSegment({
        actorVoiceID: uiDefaults.defaultActorVoiceID ?? "",
        languageOverride: uiDefaults.defaultLanguage,
        speedFactor: uiDefaults.defaultSpeedFactor,
        volumePercent: uiDefaults.defaultVolumePercent,
      }),
      afterIndex,
    );
    handleClose();
  };

  const handleAddPause = () => {
    addSegment(createPauseSegment(3000), afterIndex);
    handleClose();
  };

  const handleAddMedia = () => {
    addSegment(createMediaSegment(), afterIndex);
    handleClose();
  };

  if (!isEditable) return null;

  return (
    <>
      <Tooltip title={t(Resources.JobDetail.AddSegmentTooltip)} placement="top">
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            width: 20,
            height: 20,
            mx: 0.25,
            opacity: 0.4,
            transition: "opacity 150ms",
            color: "text.secondary",
            alignSelf: "center",
            flexShrink: 0,
            "&:hover": { opacity: 1 },
          }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        <MenuItem onClick={handleAddVoice} dense>
          <ListItemIcon sx={{ color: "inherit" }}>
            <MicIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t(Resources.JobDetail.AddVoiceSegment)}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddPause} dense>
          <ListItemIcon sx={{ color: "inherit" }}>
            <PauseCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t(Resources.JobDetail.AddPause)}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddMedia} dense>
          <ListItemIcon sx={{ color: "inherit" }}>
            <MusicNoteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t(Resources.JobDetail.AddBackgroundAudio)}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
